import React, {ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useRef} from "react";
import {LiveblogTeaserCoreWidgetProps, LiveblogTeaserWidgetApi} from "./LiveblogTeaserWidgetProps";
import {
  DefaultConsentProvider,
  LiveblogEvent,
  LiveblogRenderContext,
  LiveblogRenderContextProvider,
  TeaserWrapper,
  TrackingFunction
} from "@webng/liveblog";
import {TeaserWidget} from "@webng/liveblog/src/teaser-widget/TeaserWidget";
import * as externalUserProvider from "../user";
import {IEvent} from "@webng-types/write-model";
import {useLiveblogTeaserAnalyticsAgent} from "./useLiveblogTeaserAnalyticsAgent";
import {fireTickarooLiveblogTeaserInitEvent, fireTickarooLiveblogTeaserTracking} from "../events";
import {useTeaserWidgetEvents} from "@webng/liveblog/src/teaser-widget/useTeaserWidgetEvents";

type LiveblogTeaserCoreControllerProps = Omit<LiveblogTeaserCoreWidgetProps, "locale">

function LiveblogTeaserWidgetCoreImp({ liveblogId, sharedThemeConfiguration, liveblogTeaserThemeConfiguration, css, initialData, disableTracking, liveblogUrl, title }: LiveblogTeaserCoreControllerProps, ref: ForwardedRef<LiveblogTeaserWidgetApi>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const agent = useLiveblogTeaserAnalyticsAgent(liveblogId, sharedThemeConfiguration.useCookies === 'all', disableTracking);

  const trackingFunction: TrackingFunction = useCallback((e: LiveblogEvent) => {
    if(agent) {
      agent.track(e)
      if (wrapperRef.current && wrapperRef.current.parentElement) {
        fireTickarooLiveblogTeaserTracking(wrapperRef.current.parentElement, e)
      }
    }
  }, [agent])

  // dispatch init event
  useEffect(() => {
    if (wrapperRef.current && wrapperRef.current.parentElement) {
      fireTickarooLiveblogTeaserInitEvent(wrapperRef.current.parentElement, { liveblogId: liveblogId })
    }
  }, [liveblogId])

  useEffect(() => {
    trackingFunction({ t: 't_ini' })
  }, [trackingFunction])

  const renderContext: Partial<LiveblogRenderContext> = useMemo(() => {
    const useFunctionalCookies = sharedThemeConfiguration.useCookies === 'all' || sharedThemeConfiguration.useCookies === 'functional'
    return {
      consentProvider: new DefaultConsentProvider(useFunctionalCookies),
      webEmbedDefaultProviderConstraint: 'disabled',
      webEmbedProviderConstraints: {},
      showEventSharing: 'off',
      layoutAuthorPosition: 'off',
      commentsMode: 'disabled',
      externalUserProvider: externalUserProvider,
      uniqueId: initialData.uniqueId,
      useFunctionalCookies,
      trackingFunction: trackingFunction
    }
  }, [sharedThemeConfiguration.useCookies, initialData.uniqueId, trackingFunction]);


  const onClick = useCallback((event: IEvent) => {
    trackingFunction({t: 'a_tli', m: event.local_id})
  }, [trackingFunction])

  const onGotoLiveblogClick = useCallback((e: React.MouseEvent) => {
    trackingFunction({t: 'a_tla'})
  }, [trackingFunction])

  const {game, events} = useTeaserWidgetEvents({gameShowResponse: initialData.gameShowResponse, content: liveblogTeaserThemeConfiguration.content, limit: liveblogTeaserThemeConfiguration.limit})

  return <LiveblogRenderContextProvider context={renderContext}>
    <TeaserWrapper
      ref={wrapperRef}
      liveblogId={liveblogId}
      invertTime={liveblogTeaserThemeConfiguration.styleInvertTime}
      layout={liveblogTeaserThemeConfiguration.layout}
      layoutLiveIndicatorStyle={liveblogTeaserThemeConfiguration.layoutLiveIndicatorStyle}
      colorScheme={sharedThemeConfiguration.colorScheme}
      layoutTimeline={liveblogTeaserThemeConfiguration.layoutTimeline}
      styleTextColor={liveblogTeaserThemeConfiguration.styleTextColor}
      styleTextColorDark={liveblogTeaserThemeConfiguration.styleTextColorDark}
      styleTextMutedColor={liveblogTeaserThemeConfiguration.styleTextMutedColor}
      styleTextMutedColorDark={liveblogTeaserThemeConfiguration.styleTextMutedColorDark}
      styleBgColor={liveblogTeaserThemeConfiguration.styleBgColor}
      styleBgColorDark={liveblogTeaserThemeConfiguration.styleBgColorDark}
      styleLinkColor={liveblogTeaserThemeConfiguration.styleLinkColor}
      styleLinkColorDark={liveblogTeaserThemeConfiguration.styleLinkColorDark}
      styleSurfaceColor={liveblogTeaserThemeConfiguration.styleSurfaceColor}
      styleSurfaceColorDark={liveblogTeaserThemeConfiguration.styleSurfaceColorDark}
      css={css}
      numberOfEvents={events.length}>

      <TeaserWidget
        title={title}
        liveblogUrl={liveblogUrl}
        onGotoLiveblogClick={onGotoLiveblogClick}
        layoutLiveIndicatorStyle={liveblogTeaserThemeConfiguration.layoutLiveIndicatorStyle}
        layoutLiveblogLink={liveblogTeaserThemeConfiguration.layoutLiveblogLink}
        layout={liveblogTeaserThemeConfiguration.layout}
        layoutWhenScoreboard={liveblogTeaserThemeConfiguration.layoutWhenScoreboard}
        showTitle={liveblogTeaserThemeConfiguration.showTitle}
        limit={liveblogTeaserThemeConfiguration.limit}
        game={game}
        events={events}
        onItemClick={onClick}
      />

    </TeaserWrapper>
  </LiveblogRenderContextProvider>
}

export const LiveblogTeaserWidgetCore = forwardRef<LiveblogTeaserWidgetApi, LiveblogTeaserCoreControllerProps>(LiveblogTeaserWidgetCoreImp)
